import { gettersListMap } from 'api/padrones';
import store from 'store/store';
import { selectPadrones } from 'store/catalogos';
import {
  CURP_PATTERN,
  digitoVerificador,
  INE_PATTERN,
  RFC_PATTERN,
  EMAIL_PATTERN,
  NO_PLUS_EMAIL_RULE,
} from 'utils/patterns';

const getPadronLabel = (tipoDePadron) => selectPadrones(store.getState())
  .find((e) => e.id === tipoDePadron)?.descripcion;
export const curpsGenericas = ['XEXX010101HNEXXXA4', 'XEXX010101MNEXXXA8'];

export const generateCURPRules = ({
  validateUnique = false,
  tipoDePadron,
  required = true,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  { required: Boolean(required) },
  ({ getFieldValue }) => ({
    validator: async (rule, value) => {
      if (value) {
        if (tipoDePadron === 15 && getFieldValue('tipo_de_persona') === 3 && curpsGenericas.includes(value)) return;
        const validado = value.toUpperCase().match(CURP_PATTERN);
        if (!validado || validado[2] !== digitoVerificador(validado[1])) {
          throw new Error('Ingrese una CURP válida.');
        }
        if (validateUnique) {
          const id = _id || getFieldValue('id');
          const propsNames = { 1: 'CURP', 15: 'curp' };
          const propName = getter ? rule.field : propsNames[tipoDePadron];
          const params = { [propName]: value };
          const matches = await (getter || gettersListMap[tipoDePadron])(params);
          const match = matches?.results.filter((e) => e.id !== id)
            .find((e) => e?.[propName] === value);
          if (match) {
            throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con esta CURP.`);
          }
        }
      }
    },
  }),
]);

export const generateINERules = ({
  validateUnique = false,
  required = true,
  id: _id,
}) => ([
  { required: Boolean(required) },
  ({ getFieldValue }) => ({
    validator: async (_, val) => {
      if (val) {
        const invalid = !INE_PATTERN.test(val);
        if (validateUnique && !invalid) {
          const id = _id || getFieldValue('id');
          const matches = await gettersListMap[1]({ INE: val });
          const [match] = matches.results.filter((e) => e.id !== id);
          if (match) {
            throw new Error(`Ya existe un ${getPadronLabel(1)} con esta INE.`);
          }
        } else if (invalid) {
          throw new Error('Ingrese una INE válida.');
        }
      }
    },
  }),
]);

export const generateRFCRules = ({
  validateUnique = false,
  tipoDePersona: _tipoDePersona,
  tipoDePersonaName,
  tipoDePadron,
  required = false,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  ({ getFieldValue }) => ({
    validator: async (rule, _value) => {
      if (required && !_value) throw new Error('El campo es requerido');
      if (_value) {
        const value = _value.toString().toUpperCase();
        const lengthsByTipoPersona = {
          1: 13, // fisica
          2: 12, // moral
        };
        const tipoDePersona = tipoDePersonaName
          ? getFieldValue(tipoDePersonaName) : _tipoDePersona;
        const invalidLength = tipoDePersona
          ? value.length !== lengthsByTipoPersona[tipoDePersona] : false;
        const invalid = !RFC_PATTERN.test(value) || invalidLength;
        if (invalid) throw new Error('Ingrese un RFC válido.');
        if (validateUnique) {
          const id = _id || getFieldValue('id');
          const propsNames = { 1: 'RFC', 15: 'rfc' };
          const propName = getter ? rule.field : propsNames[tipoDePadron];
          const params = { [propName]: value };
          const matches = await (getter || gettersListMap[tipoDePadron])(params);
          const [match] = matches.results.filter((e) => e.id !== id);
          if (match) {
            throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con esta RFC.`);
          }
        }
      }
    },
  }),
]);

export const generateEmailRules = ({
  validateUnique = false,
  tipoDePadron,
  required = false,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  { required },
  NO_PLUS_EMAIL_RULE,
  ({ getFieldValue }) => ({
    validator: async (rule, value) => {
      if (value) {
        const invalid = !EMAIL_PATTERN.test(value);
        if (validateUnique && !invalid) {
          const id = _id || getFieldValue('id');
          const propName = rule.field;
          const params = { [propName]: value };
          const matches = await (getter || gettersListMap[tipoDePadron])(params);
          const [match] = matches.results.filter((e) => e.id !== id);
          if (match) {
            throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con este correo electrónico.`);
          }
        } else if (invalid) {
          throw new Error('Ingrese un correo eletrónico válido.');
        }
      }
    },
  }),
]);

export const generatePhoneRules = ({
  validateUnique = false,
  tipoDePadron,
  required = false,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  { required: Boolean(required) },
  ({ getFieldValue }) => ({
    validator: async (rule, value) => {
      const invalid = !/^[0-9]{10}$/.test(value);
      if (validateUnique && !invalid) {
        const id = _id || getFieldValue('id');
        const propName = rule.field;
        const params = { [propName]: value };
        const matches = await (getter || gettersListMap[tipoDePadron])(params);
        const [match] = matches.results.filter((e) => e.id !== id);
        if (match) {
          throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con este número de celular.`);
        }
      }
    },
  }),
]);

// eslint-disable-next-line default-param-last
export const uniqueValidator = (data = [], current, label) => ({
  validator: async (rule, value) => {
    const exists = data.some((item) => item[rule.field] === value && item.id !== current);
    if (exists) {
      throw new Error(`${label || rule.field} debe tener un valor único`);
    }
  },
});

export const customUniqueValidator = ({
  getter,
  fieldName,
  tipoDePadron,
  label,
}) => ({ getFieldValue }) => ({
  validator: async (rule, value) => {
    const field = fieldName || rule.field;
    const params = { [field]: value };
    let res = await (getter || gettersListMap[tipoDePadron])(params);
    if (!Array.isArray(res)) {
      res = [res];
    }
    // eslint-disable-next-line eqeqeq
    if ((res?.results || res).some((e) => e?.[field] == value && getFieldValue('id') != e.id)) {
      throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron)} con ${label} ingresado`);
    }
  },
});
